import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { ItemCarrinho, useCarrinho } from '../context/CarrinhoContext';
import { CRIAR_PEDIDO, formatCurrency } from '../services/quadraService';

const MEU_SALDO_CARRINHO = gql`
  query MeuSaldoCarrinho($localId: ID!) {
    meuSaldoNoLocal(localId: $localId)
  }
`;

// Agrupa itens do carrinho em blocos consecutivos por quadra+data
const agruparItens = (itens: ItemCarrinho[]) => {
  const grupos: Record<string, ItemCarrinho[]> = {};
  itens.forEach(item => {
    const chave = `${item.quadraId}|${item.dataAgendamento}`;
    if (!grupos[chave]) grupos[chave] = [];
    grupos[chave].push(item);
  });

  const blocos: any[] = [];
  Object.values(grupos).forEach(grupo => {
    const sorted = [...grupo].sort((a, b) => a.horaInicio.localeCompare(b.horaInicio));
    let bloco: any[] = [sorted[0]];
    for (let i = 1; i < sorted.length; i++) {
      if (bloco[bloco.length - 1].horaFim === sorted[i].horaInicio) {
        bloco.push(sorted[i]);
      } else {
        blocos.push(bloco);
        bloco = [sorted[i]];
      }
    }
    blocos.push(bloco);
  });
  return blocos;
};

const CarrinhoPainel: React.FC = () => {
  const navigate = useNavigate();
  const { itens, localId, localNome, removerItem, limpar } = useCarrinho();
  const [erro, setErro] = useState('');
  const [expandido, setExpandido] = useState(false);

  const [criarPedido, { loading }] = useMutation(CRIAR_PEDIDO);

  const { data: saldoData } = useQuery(MEU_SALDO_CARRINHO, {
    variables: { localId: localId ?? '' },
    skip: !localId,
    fetchPolicy: 'cache-first',
  });
  const saldoCredito: number = saldoData?.meuSaldoNoLocal ?? 0;

  if (itens.length === 0) return null;

  const totalValor = itens.reduce((acc, i) => acc + (i.valor ?? 0), 0);
  const totalOriginal = itens.reduce((acc, i) => acc + (i.valorOriginal ?? i.valor ?? 0), 0);
  const temDesconto = itens.some(i => i.valorOriginal != null);
  const temValor = itens.some(i => i.valor !== undefined);

  const blocos = agruparItens(itens);

  const handleReservar = async () => {
    setErro('');
    try {
      const { data } = await criarPedido({
        variables: {
          input: {
            localId,
            itens: blocos.map(bloco => ({
              quadraId: bloco[0].quadraId,
              dataAgendamento: bloco[0].dataAgendamento,
              horaInicio: bloco[0].horaInicio,
              horaFim: bloco[bloco.length - 1].horaFim,
            })),
          },
        },
      });
      limpar();
      navigate(`/quadras/pedido/${data.criarPedidoAgendamento.id}`);
    } catch (e: any) {
      setErro(e?.graphQLErrors?.[0]?.message ?? e.message ?? 'Erro ao criar pedido.');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: 320,
        zIndex: 1050,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        borderRadius: 16,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        className="bg-primary-gradiant d-flex align-items-center justify-content-between px-3 py-2"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpandido(v => !v)}
      >
        <span className="text-white fw-700 font-xsss">
          <i className="feather-shopping-cart me-2"></i>
          {itens.length} horário{itens.length !== 1 ? 's' : ''} selecionado{itens.length !== 1 ? 's' : ''}
          {temValor && (
            <span className="ms-2 opacity-75">
              {temDesconto && <span className="text-decoration-line-through me-1" style={{ opacity: 0.6 }}>{formatCurrency(totalOriginal)}</span>}
              {formatCurrency(totalValor)}
            </span>
          )}
        </span>
        <i className={`feather-chevron-${expandido ? 'down' : 'up'} text-white`}></i>
      </div>

      {expandido && (
        <div className="p-3">
          <p className="font-xssss text-grey-500 mb-2">
            <i className="feather-map-pin me-1"></i>{localNome}
          </p>

          {/* Lista de blocos */}
          <div style={{ maxHeight: 200, overflowY: 'auto' }} className="mb-2">
            {blocos.map((bloco, i) => (
              <div key={i} className="d-flex align-items-center justify-content-between mb-1 py-1 border-bottom">
                <div>
                  <p className="font-xssss fw-600 text-grey-900 mb-0">{bloco[0].quadraNome}</p>
                  <span className="font-xssss text-grey-500">
                    {new Date(bloco[0].dataAgendamento + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                    {' · '}{bloco[0].horaInicio} – {bloco[bloco.length - 1].horaFim}
                  </span>
                  {bloco.some((s: ItemCarrinho) => s.valor !== undefined) && (
                    <span className="font-xssss fw-600 text-success d-block">
                      {bloco.some((s: ItemCarrinho) => s.valorOriginal != null) && (
                        <span className="text-grey-400 text-decoration-line-through me-1">
                          {formatCurrency(bloco.reduce((acc: number, s: ItemCarrinho) => acc + (s.valorOriginal ?? s.valor ?? 0), 0))}
                        </span>
                      )}
                      {formatCurrency(bloco.reduce((acc: number, s: ItemCarrinho) => acc + (s.valor ?? 0), 0))}
                    </span>
                  )}
                </div>
                <button
                  className="btn btn-sm btn-light rounded-xl px-2 py-1"
                  onClick={() => bloco.forEach((item: any) => removerItem(item.quadraId, item.horaInicio))}
                  title="Remover"
                >
                  <i className="feather-x font-xssss"></i>
                </button>
              </div>
            ))}
          </div>

          {erro && <div className="alert alert-danger font-xssss py-2 mb-2">{erro}</div>}

          {temValor && (
            <div className="pt-1 border-top mb-2">
              {temDesconto && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="font-xssss text-grey-500">Subtotal</span>
                    <span className="font-xssss text-grey-400 text-decoration-line-through">{formatCurrency(totalOriginal)}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="font-xssss text-success fw-600">Desconto aluno</span>
                    <span className="font-xssss text-success fw-600">-{formatCurrency(totalOriginal - totalValor)}</span>
                  </div>
                </>
              )}
              {saldoCredito > 0 && (
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="font-xssss text-success fw-600"><i className="bi bi-wallet2 me-1"></i>Crédito</span>
                  <span className="font-xssss text-success fw-600">-{formatCurrency(Math.min(saldoCredito, totalValor))}</span>
                </div>
              )}
              <div className="d-flex justify-content-between align-items-center">
                <span className="font-xssss text-grey-600 fw-600">Total</span>
                <span className="font-xsss fw-700 text-success">{formatCurrency(Math.max(0, totalValor - saldoCredito))}</span>
              </div>
            </div>
          )}

          <button
            className="btn bg-primary-gradiant text-white rounded-xl fw-600 font-xsss w-100 mb-2"
            onClick={handleReservar}
            disabled={loading}
          >
            {loading ? 'Reservando...' : <><i className="feather-check me-1"></i>Reservar tudo</>}
          </button>
          <button
            className="btn btn-light rounded-xl fw-600 font-xssss w-100"
            onClick={() => { limpar(); setErro(''); }}
          >
            Limpar carrinho
          </button>
        </div>
      )}
    </div>
  );
};

export default CarrinhoPainel;
